import {
    Component,
    EventEmitter,
    OnInit,
    OnDestroy,
    Output,
    ChangeDetectorRef,
    AfterViewInit,
    Inject
} from '@angular/core';
import { Observable, BehaviorSubject, of } from 'rxjs';
import { FetchArgs, PagingToken } from 'src/app/shared/models/core';
import {
    Expertise,
    GridData,
    GridDataItems,
    GridLookups,
    OfferType,
    ProfessionsLookups,
    Statuses
} from 'src/app/shared/models/offers.model';
import { switchMap, tap, takeWhile, share, catchError, finalize, takeUntil } from 'rxjs/operators';
import { GridDataResult } from '@progress/kendo-angular-grid';
import { IdentityService } from 'src/app/core/identity.service';
import { GridComponentService } from 'src/app/shared/grid/grid-component.service';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { OfferStatus } from 'src/app/shared/models/enums/offers.enum';
import { MAT_DIALOG_DATA, MatDialogRef, MatDialogModule } from '@angular/material/dialog';
import { RecruitingActions, recruitingSelectors } from 'src/app/root-store/recruiting-store';
import { Store, select } from '@ngrx/store';
import { WINDOW } from 'src/app/core/core.module';
import { UnsubscribeOnDestroy } from 'src/app/shared/utilities/virtual-images/unsubscribe-ondestroy';
import { FeatureFlag } from 'src/app/shared/models/enums/feature-flag';
import { RecruitingDealsService } from 'src/app/recruiting/deals/deals.service';
import { LDFeatureManager } from 'src/app/shared/services/ld-feature-manager';
import { MarginCalculatorMarginService } from '../services/margin-calculator-margin.service';
import { MarginCalculatorPageActions } from '../store/actions';
import { AsyncPipe, LowerCasePipe, DatePipe } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { GridClearFilterPopupComponent } from '../../shared/grid/filters/grid-clear-filter-popup.component';
import { GridTextFilterComponent } from '../../shared/grid/filters/grid-text-filter.component';
import { GridFilterLayoutComponent } from '../../shared/grid/filters/base-filter-cell-layout.component';
import { GridColumnDirective } from '../../shared/grid/grid-column.directive';
import { GridComponent } from '../../shared/grid/grid.component';

@UntilDestroy()
@Component({
    selector: 'margin-offer-link-dialog',
    templateUrl: './margin-offer-link-dialog.component.html',
    styleUrls: ['./margin-offer-link-dialog.component.scss'],
    standalone: true,
    imports: [
        MatDialogModule,
        GridComponent,
        GridColumnDirective,
        GridFilterLayoutComponent,
        GridTextFilterComponent,
        GridClearFilterPopupComponent,
        MatButtonModule,
        AsyncPipe,
        LowerCasePipe,
        DatePipe
    ]
})
export class MarginOfferLinkDialogComponent extends UnsubscribeOnDestroy implements OnInit, OnDestroy, AfterViewInit {
    reqInput: string[] = ['candidate-offer', 'account-manager'];
    @Output() goTo: EventEmitter<number> = new EventEmitter();
    queryPage = 'perPage';
    gridName = 'nova-am-offers-grid';
    data$: Observable<PagingToken<GridData[]>>;
    offersData: PagingToken<GridData[]>;
    fetchArgs$: BehaviorSubject<FetchArgs>;
    isLoading = false;
    isClearFilterButtonEnabled = false;
    lookups$: Observable<GridLookups>;
    lookupStatuses: Statuses[];
    lookupProfessions: ProfessionsLookups[];
    lookupExpertise: Expertise[] = [];
    perPageItems = null;
    offersLength = 0;
    offersTotal = 0;
    componentActive = true;
    queriesFilter = null;
    defaultFetchArgs: FetchArgs;
    offerId: number;
    marginId: number;
    candidateName: string;
    candidateId?: number;
    isLinkMarginButtonDisabled: boolean = true;
    isLinking: boolean = false;
    useNewContractChangeTypeEndpoint: boolean = false;
    noRecordsMessage =
        "No offers found for this candidate. Please contact the facility's Account Manager to have an Offer created.";
    readonly featureFlag = FeatureFlag;

    constructor(
        private readonly identityService: IdentityService,
        private readonly gridService: GridComponentService,
        private readonly _offersService: RecruitingDealsService,
        private readonly _marginService: MarginCalculatorMarginService,
        private readonly _cdRef: ChangeDetectorRef,
        private readonly _store: Store<{}>,
        private readonly _featureManager: LDFeatureManager,
        protected appIdentityService: IdentityService,
        @Inject(WINDOW) private readonly _window: Window,
        private readonly _dialogRef: MatDialogRef<MarginOfferLinkDialogComponent>,
        @Inject(MAT_DIALOG_DATA) public data: any
    ) {
        super();
        this.marginId = data?.marginId;
        this.candidateName = data?.candidateName;
        this.candidateId = data?.candidateId;
    }

    ngOnInit() {
        this.initOffers();
        if (!this.offersData || this.offersData.total === 0) {
            this.data$.subscribe((data) => {
                this.offersLength = data.data.length;
                this.offersTotal = data.total;
            });
        }

        this._featureManager
            .isEnabled(FeatureFlag.UseNewContractChangeTypeEndpoint)
            .pipe(untilDestroyed(this))
            .subscribe((flagIsEnabled) => {
                this.useNewContractChangeTypeEndpoint = flagIsEnabled;
            });
    }

    initOffers() {
        let filter: any = !this.candidateId ? { candidateName: this.candidateName } : { candidateId: this.candidateId };

        filter = { ...filter, offerType: OfferType.Direct, offerStatus: OfferStatus.RecruiterReview };

        this.defaultFetchArgs = {
            filter,
            sort: { field: 'createdDate', dir: 'asc' },
            skip: 0,
            pageSize: 25
        };

        this.loadState(this.defaultFetchArgs);
        this.lookups$ = this._offersService.lookups();

        this.data$ = this.fetchArgs$.pipe(
            tap(() => {
                this.isLoading = true;
            }),
            switchMap((args: FetchArgs) =>
                this._offersService.getGridData(args).pipe(
                    finalize(() => (this.isLoading = false)),
                    catchError(() => {
                        return of({} as GridDataResult);
                    })
                )
            ),
            share()
        );

        this.lookups$.pipe(takeWhile(() => this.componentActive)).subscribe((data) => {
            this.lookupStatuses = data.statuses;
            this.lookupProfessions = data.professions;
            data.expertises.forEach((exp) => {
                this.lookupExpertise.push({
                    id: exp.id,
                    name: exp.name,
                    abbreviation: exp.abbreviation,
                    sort: exp.sort,
                    professionExpertiseCodes: exp.professionExpertiseCodes,
                    searchableVal: exp.name
                });
            });
        });
    }

    ngAfterViewInit() {
        this._cdRef.detectChanges();
    }

    load(args: FetchArgs) {
        this.fetchArgs$.next(args);
    }

    loadState(args: FetchArgs) {
        const userFetchArgs = this.gridService.getFetchArgs(this.gridName);
        const bs = new BehaviorSubject<FetchArgs>(userFetchArgs || args);
        this.fetchArgs$ = bs;
    }

    rowSelected(item: GridDataItems[]): void {
        if (item[0]) {
            this.offerId = item[0].id;
            this.isLinkMarginButtonDisabled = false;
        } else {
            this.isLinkMarginButtonDisabled = true;
            this.offerId = null;
        }
    }

    openMarginCalculator(marginId: number): void {
        let queryParamString = `?showIsSavedBanner=true`;
        if (!this.useNewContractChangeTypeEndpoint) {
            queryParamString = `${queryParamString}&previousMarginId=${this.marginId}`;
        }
        this._window.open(
            `${this._window.location.origin}/#/margin-calculator/${this.identityService.current.contactId}/margin/${marginId}${queryParamString}`,
            '_blank',
            'width=1366,height=768,resizable=1,scrollbars=1,titlebar=0,location=0,status=0'
        );
    }

    linkMarginToOffer() {
        if (!this.isLinking && this.offerId) {
            this.isLinking = true;
            this.isLoading = true;
            this._store.dispatch(
                RecruitingActions.DirectOfferImportMarginActions.createDirectOfferMarginViaImport({
                    offerId: this.offerId,
                    importFromMarginId: this.marginId
                })
            );

            this._store
                .pipe(select(recruitingSelectors.selectCreatedDirectOfferMarginViaImport), untilDestroyed(this))
                .subscribe((marginId) => {
                    if (marginId) {
                        this._store.dispatch(
                            RecruitingActions.DirectOfferImportMarginActions.resetMarginId({ marginId: undefined })
                        );
                        this._dialogRef.close(marginId);
                        this._store.dispatch(MarginCalculatorPageActions.linkToOfferDialogClosed());
                        this.openMarginCalculator(marginId);
                        this._marginService.setContinueWithoutLinkingClicked(true);
                        this._store.dispatch(
                            RecruitingActions.DirectOfferImportMarginActions.resetMarginId({ marginId: undefined })
                        );
                    }
                });
        }
    }

    closeDialog(argument: number = null): void {
        if (argument === 0) {
            this._store.dispatch(MarginCalculatorPageActions.linkToOfferDialogClosed());
            this._marginService.setContinueWithoutLinkingClicked(true);
        }
        this._dialogRef.close(argument);
    }

    private _isLdFeatureFlagEnabled(featureName: FeatureFlag): boolean {
        let isFeatureEnabled = false;
        this._featureManager
            .isEnabled(featureName)
            .pipe(takeUntil(this._cleanup$))
            .subscribe((flagIsEnabled) => {
                isFeatureEnabled = flagIsEnabled;
            });
        return isFeatureEnabled;
    }
}
